import React, { useEffect } from "react";
import { DatePicker, ServiceSelector, BookingForm } from "../";
import { Service, BookingData, Professional } from "../../../types";
import { useBookingWizardState } from "../../../hooks/useBookingWizardState";
import { BackArrow, StepIndicator } from "../../shared";

interface CarBookingWizardProps {
  selectedDate: string;
  selectedService: Service | null;
  selectedSubServices?: number[];
  professionals: Professional[];
  loading: boolean;
  error: string | null;
  phoneNumber: string;
  onDateChange: (date: string) => void;
  onServiceChange: (service: Service) => void;
  onSubServicesChange?: (subServiceIds: number[]) => void;
  onProfessionalChange: (professional: Professional) => void;
  bookAppointment: (bookingData: BookingData) => Promise<void>;
}

export const CarBookingWizard: React.FC<CarBookingWizardProps> = ({
  selectedDate,
  selectedService,
  selectedSubServices = [],
  loading,
  error,
  phoneNumber,
  onDateChange,
  onServiceChange,
  onSubServicesChange,
  bookAppointment,
}) => {
  const {
    serviceSelectionComplete,
    dateSelectionComplete,
    setServiceSelectionComplete,
    resetDateSelection,
    handleDateContinue,
  } = useBookingWizardState();

  // Reset states when selections change
  useEffect(() => {
    if (!selectedDate) {
      resetDateSelection();
    }
  }, [selectedDate, resetDateSelection]);

  useEffect(() => {
    if (selectedService) {
      setServiceSelectionComplete(false);
    }
  }, [selectedService, setServiceSelectionComplete]);

  // Navigation functions
  const handleBackToDate = () => {
    onDateChange("");
    resetDateSelection();
  };

  // Check if error is about client not found
  const isClientNotFoundError =
    error?.toLowerCase().includes("no client found") ||
    error?.toLowerCase().includes("cliente não encontrado");

  // Determine current step
  let currentStep = 1;
  if (dateSelectionComplete && !serviceSelectionComplete) {
    currentStep = 2;
  } else if (serviceSelectionComplete && (!error || isClientNotFoundError)) {
    currentStep = 3;
  }

  const stepLabels = ["Data", "Serviço", "Confirmação"];

  return (
    <div className="w-full mx-auto min-h-[calc(100vh-96px)] flex flex-col">
      <div className="w-full mx-auto p-6 bg-white flex-1 overflow-y-auto">
        <StepIndicator
          currentStep={currentStep}
          totalSteps={3}
          stepLabels={stepLabels}
        />

        {/* Step 1: Date Selection */}
        {!dateSelectionComplete && (
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            onContinue={() => {
              if (selectedDate) {
                handleDateContinue();
              }
            }}
          />
        )}

        {/* Step 2: Service Selection */}
        {dateSelectionComplete &&
          !serviceSelectionComplete &&
          !isClientNotFoundError && (
            <div>
              <BackArrow onClick={handleBackToDate} text="Voltar para data" />
              <ServiceSelector
                selectedService={selectedService}
                selectedSubServices={selectedSubServices}
                onServiceChange={onServiceChange}
                hasError={!!error}
                onBackToDate={handleBackToDate}
                onSubServicesChange={onSubServicesChange}
                onContinue={() => setServiceSelectionComplete(true)}
              />
              {error && !isClientNotFoundError && (
                <div className="mt-6 max-w-2xl mx-auto">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-xl">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="text-red-400 text-xl">⚠️</span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700 font-medium">
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Step 3: Booking Form */}
        {dateSelectionComplete &&
          serviceSelectionComplete &&
          (!error || isClientNotFoundError) && (
            <div>
              <BackArrow
                onClick={() => setServiceSelectionComplete(false)}
                text="Voltar para serviços"
              />
              <BookingForm
                selectedDate={selectedDate}
                selectedTimeSlot={null}
                selectedService={selectedService!}
                selectedSubServices={selectedSubServices}
                selectedProfessional={null}
                phoneNumber={phoneNumber}
                onSubmit={bookAppointment}
                clientNotFound={isClientNotFoundError}
                loading={loading}
              />
            </div>
          )}
      </div>
    </div>
  );
};
